/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import tutorTableData from "Views/people/tutor/data/tutorTableData";
// import TestUserTableData from "Views/TestUser/data/TestUserTableData"

// import IncomeBreakDownTabelData from "Views/Report/IncomeBreakDown/data/IncomeBreakDownTableData"
import IncomeBreakDownTabelData from "Views/Report/IncomeBreakDown/data/IncomeBreakDownTableData";
import { useState, useEffect } from "react";
import { getIncomeBreakdown } from "services/financeService";

import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import ArgonInput from "components/ArgonInput";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import PaginationBar from "components/PaginationBar/PaginationBar";
import ArgonButton from "components/ArgonButton";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function Tables() {
  // const { columns, rows } = IncomeBreakDownTabelData;
  const [dob, setDob] = useState(dayjs());
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [activeTab, setActiveTab] = useState("Clients");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [tableRows, setTableRows] = useState([]);

  const columns = [
    { name: "sno", align: "center" },
    { name: "name", align: "left" },
    { name: "Tuition fee", align: "left" },
    { name: "Net Salary", align: "center" },
    { name: "Margin", align: "center" },
  ];

  useEffect(() => {
    if (activeTab === "Category_Standard") {
      setTableRows(IncomeBreakDownTabelData.rows);
    } else {
      setTableRows(
        data.map((item, index) => ({
          sno: (
            <ArgonTypography variant="caption" fontWeight="medium" color="text">
              {(currentPage - 1) * rowsPerPage + index + 1}
            </ArgonTypography>
          ),
          name: (
            <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
              <ArgonBox mr={2}>
                <ArgonAvatar
                  src={item.tutorProfileImage || item.parentProfileImage}
                  alt={item.tutorName || item.parentName}
                  size="sm"
                  variant="rounded"
                />
              </ArgonBox>
              <ArgonBox display="flex" flexDirection="column">
                <ArgonTypography textTransform="none" variant="button" fontWeight="medium">
                  {item.tutorName || item.parentName}
                </ArgonTypography>
                <ArgonTypography variant="caption" color="secondary">
                  {item.tutorEmail || item.parentEmail}
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
          ),
          "Tuition fee": (
            <ArgonTypography variant="caption" color="success" fontWeight="medium">
              ₹{item.tuitionFee}
            </ArgonTypography>
          ),
          "Net Salary": (
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              ₹{item.netSalary}
            </ArgonTypography>
          ),
          Margin: (
            <ArgonTypography variant="caption" color="info" fontWeight="medium">
              ₹{item.margin} ({item.marginPercentage}%)
            </ArgonTypography>
          ),
        }))
      );
    }
  }, [data, activeTab, currentPage, rowsPerPage]);

  useEffect(() => {
    fetchIncomeBreakdown();
  }, [activeTab, fromDate, toDate, search, currentPage, rowsPerPage]);

  const fetchIncomeBreakdown = async () => {
    if (activeTab === "Category_Standard") {
      // Use dummy data for Category_Standard
      setTableRows(IncomeBreakDownTabelData.rows);
      setTotalItems(IncomeBreakDownTabelData.rows.length);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = {
        groupBy: activeTab === "Clients" ? "parent" : "tutor",
        from: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
        to: toDate ? toDate.format("YYYY-MM-DD") : undefined,
        search: search || undefined,
        page: currentPage,
        limit: rowsPerPage,
      };
      const response = await getIncomeBreakdown(params);
      setData(response.data);
      setTotalItems(response.pagination.totalRecords);
    } catch (error) {
      console.error("Error fetching income breakdown:", error);
    } finally {
      setLoading(false);
    }
  };

  const tabsData = [
    { label: "Clients", content: "" },
    { label: "Tutors", content: "" },
    { label: "Category_Standard", content: "" },
  ];

  // Prepare chart data
  const chartData = {
    labels: data.map((item) => item.tutorName || item.parentName || ""),
    datasets: [
      {
        label: "Net Salary",
        data: data.map((item) => item.netSalary || 0),
        backgroundColor: "rgba(94, 200, 227, 0.7)",
        borderColor: "rgba(94, 200, 227, 1)",
        borderWidth: 1,
      },
      {
        label: "Tuition Fee",
        data: data.map((item) => item.tuitionFee || 0),
        backgroundColor: "rgba(129, 195, 255, 0.7)",
        borderColor: "rgba(129, 195, 255, 1)",
        borderWidth: 1,
      },
      {
        label: "Margin",
        data: data.map((item) => item.margin || 0),
        backgroundColor: "rgba(255, 228, 163, 0.7)",
        borderColor: "rgba(255, 228, 163, 1)",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
          },
          padding: 15,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            label += "₹" + context.parsed.y.toLocaleString("en-IN");
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return "₹" + value.toLocaleString("en-IN");
          },
        },
      },
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
        },
      },
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={2}>
        <ArgonBox mb={2}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Income Breakdown
              </ArgonTypography>

              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonBox sx={{ height: "36px" }}>
                  <ApnaTabs
                    tabsData={tabsData}
                    onTabChange={(tab) => {
                      setActiveTab(tab);
                      setCurrentPage(1);
                    }}
                  />
                </ArgonBox>
                <ArgonBox sx={{ height: "36px" }} display="flex" gap={2} alignItems="center">
                  <CustomDatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => {
                      setFromDate(newValue);
                      setCurrentPage(1);
                    }}
                    width={160}
                    maxDate={toDate || dayjs()} // future date disable or toDate
                  />
                  <CustomDatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => {
                      setToDate(newValue);
                      setCurrentPage(1);
                    }}
                    width={160}
                    minDate={fromDate}
                    maxDate={dayjs()} // future date disable
                  />
                  {/* <ArgonInput
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{ width: 200 }}
                  /> */}
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginLeft: "10px" }}
                  >
                    Filter
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            {/* Chart Section */}
            {!loading && data.length > 0 && activeTab !== "Category_Standard" && (
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Income Breakdown Chart
                </ArgonTypography>
                <ArgonBox sx={{ height: "400px", position: "relative" }}>
                  <Bar data={chartData} options={chartOptions} />
                </ArgonBox>
              </ArgonBox>
            )}

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching{" "}
                    {activeTab === "Clients"
                      ? "clients"
                      : activeTab === "Tutors"
                      ? "tutors"
                      : "data"}
                    ...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : data.length === 0 ? (
                <ArgonBox display="flex" flexDirection="column" alignItems="center" p={5}>
                  <ArgonTypography variant="h6" color="text" mb={1}>
                    No income data found
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text">
                    Try adjusting your filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={tableRows} />
              )}
            </ArgonBox>
            <PaginationBar
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              totalItems={totalItems}
              onPageChange={(event, page) => setCurrentPage(page)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setCurrentPage(1);
              }}
            />
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
